import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft, Users, Tag, TrendingUp, Shield, UserCog, ShoppingBag, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AdminOverviewTab, AdminUsersTab, AdminMerchantsTab, AdminInfluencersTab,
  AdminCouponsTab, AdminCategoriesTab, AdminKeysTab, AdminSubscriptionsTab,
  AdminFinancialTab, AdminAudiobooksTab, AdminBannersTab, AdminTestimonialsTab
} from '../components/admin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState([]);
  const [merchantAnalytics, setMerchantAnalytics] = useState([]);
  const [couponAnalytics, setCouponAnalytics] = useState([]);
  const [categoryAnalytics, setCategoryAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [accessKeys, setAccessKeys] = useState([]);
  const [newKeyPlan, setNewKeyPlan] = useState('premium');
  const [influencerAnalytics, setInfluencerAnalytics] = useState([]);
  const [influencerInvites, setInfluencerInvites] = useState([]);
  const [newInviteName, setNewInviteName] = useState('');
  const [virtualKeyPlan, setVirtualKeyPlan] = useState('premium');
  const [generatedVirtualKey, setGeneratedVirtualKey] = useState('');
  const [financialData, setFinancialData] = useState(null);
  const [audiobooksMerchant, setAudiobooksMerchant] = useState([]);
  const [audiobooksInfluencer, setAudiobooksInfluencer] = useState([]);
  const [uploadingAB, setUploadingAB] = useState(false);
  const [tutorialVideo, setTutorialVideo] = useState(null);
  const [uploadingTutorial, setUploadingTutorial] = useState(false);
  const [banners, setBanners] = useState([]);
  const [bannerForm, setBannerForm] = useState({
    title: '', url: '', description: '', tipo: 'global', ordem_exibicao: 1,
    state: '', city: '', category: '', merchant_name: '', contact_whatsapp: '',
    monthly_value: '', months_contracted: 1, renewal_alert_days: 7
  });
  const [bannerImage, setBannerImage] = useState(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerCities, setBannerCities] = useState([]);
  const [generatingPayLink, setGeneratingPayLink] = useState(null);
  const [subHealth, setSubHealth] = useState(null);
  const [adminTestimonials, setAdminTestimonials] = useState([]);
  const [adminUser, setAdminUser] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('session_token')}` };

  useEffect(() => { fetchData(); fetchAdminUser(); }, []);

  const fetchAdminUser = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { headers });
      setAdminUser(res.data);
    } catch (err) { console.warn('Admin user fetch error:', err.message); }
  };

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, merchantsRes, couponsRes, catsRes, keysRes, infRes, invitesRes, finRes, abMRes, abIRes, bannersRes, subHealthRes, testimonialsRes, tutorialRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/dashboard`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/users`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/merchants`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/coupons`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/categories`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/access-keys`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/influencers`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/influencer-invites`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/financial`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/audiobooks/merchant`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/audiobooks/influencer`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/banners`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/subscription-health`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/testimonials`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/tutorial-video`, { headers }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setUserAnalytics(usersRes.data);
      setMerchantAnalytics(merchantsRes.data);
      setCouponAnalytics(couponsRes.data);
      setCategoryAnalytics(catsRes.data);
      setAccessKeys(keysRes.data);
      setInfluencerAnalytics(infRes.data);
      setInfluencerInvites(invitesRes.data);
      setFinancialData(finRes.data);
      setAudiobooksMerchant(abMRes.data);
      setAudiobooksInfluencer(abIRes.data);
      setBanners(bannersRes.data);
      setSubHealth(subHealthRes.data);
      setTutorialVideo(tutorialRes.data);
      setAdminTestimonials(testimonialsRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate('/');
      else toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // === Handler Functions ===
  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${userId}/role`, { role: newRole }, { headers });
      toast.success(`Role atualizada para ${newRole}`);
      fetchData();
      setShowRoleDialog(false);
    } catch { toast.error('Erro ao atualizar role'); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, { headers });
      toast.success('Usuario excluido');
      fetchData();
      setShowDeleteDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!confirm('Excluir este cupom?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/coupons/${couponId}`, { headers });
      toast.success('Cupom excluido');
      fetchData();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleCreateKey = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/access-keys`, { plan: newKeyPlan }, { headers });
      toast.success(`Chave criada: ${res.data.key}`);
      fetchData();
    } catch { toast.error('Erro ao criar chave'); }
  };

  const handleDeleteKey = async (key) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/access-keys/${key}`, { headers });
      toast.success('Chave excluida');
      fetchData();
    } catch { toast.error('Erro ao excluir chave'); }
  };

  const handleCreateInvite = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/influencer-invites`, { name: newInviteName }, { headers });
      toast.success(`Convite criado: ${res.data.key}`);
      setNewInviteName('');
      fetchData();
    } catch { toast.error('Erro ao criar convite'); }
  };

  const handleDeleteInvite = async (key) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/influencer-invites/${key}`, { headers });
      toast.success('Convite excluido');
      fetchData();
    } catch { toast.error('Erro ao excluir convite'); }
  };

  const handleGenerateVirtualKey = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/virtual-influencer/generate-key`, { plan: virtualKeyPlan });
      setGeneratedVirtualKey(res.data.key);
      toast.success(`Chave virtual criada: ${res.data.key}`);
    } catch { toast.error('Erro ao gerar chave virtual'); }
  };

  const handleUploadAudiobook = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.mp4')) { toast.error('Apenas arquivos .mp4'); return; }
    const title = prompt('Titulo do AudioBook:');
    if (!title) return;
    setUploadingAB(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('target', target);
      await axios.post(`${BACKEND_URL}/api/admin/audiobooks/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('AudioBook enviado com sucesso!');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao enviar'); }
    finally { setUploadingAB(false); }
  };

  const handleDeleteAudiobook = async (id) => {
    if (!confirm('Excluir este audiobook?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/audiobooks/${id}`, { headers });
      toast.success('AudioBook excluido');
      fetchData();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleUploadTutorial = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingTutorial(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${BACKEND_URL}/api/admin/tutorial-video/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Video tutorial enviado!');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao enviar'); }
    finally { setUploadingTutorial(false); }
  };

  const handleToggleTutorial = async () => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/tutorial-video/toggle`, {}, { headers });
      toast.success(res.data.visible ? 'Video visivel na Landing' : 'Video oculto da Landing');
      fetchData();
    } catch { toast.error('Erro ao alterar visibilidade'); }
  };

  const handleDeleteTutorial = async () => {
    if (!confirm('Excluir o video tutorial?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/tutorial-video`, { headers });
      toast.success('Video tutorial excluido');
      fetchData();
    } catch { toast.error('Erro ao excluir'); }
  };

  const sortData = (data, field, dir) => {
    return [...data].sort((a, b) => {
      const va = a[field] ?? '';
      const vb = b[field] ?? '';
      if (typeof va === 'number') return dir === 'asc' ? va - vb : vb - va;
      return dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const exportCSV = (data, filename, columns) => {
    const header = columns.map(c => c.label).join(';');
    const rows = data.map(row =>
      columns.map(c => {
        const val = typeof c.get === 'function' ? c.get(row) : (row[c.key] ?? '');
        return String(val).replace(/;/g, ',').replace(/\n/g, ' ');
      }).join(';')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportado com sucesso');
  };

  const exportUsers = () => exportCSV(userAnalytics, 'usuarios', [
    { label: 'Nome', key: 'name' }, { label: 'Email', key: 'email' },
    { label: 'Cidade', key: 'city' }, { label: 'Bairro', key: 'neighborhood' },
    { label: 'WhatsApp', key: 'whatsapp' }, { label: 'Pontos', key: 'gamification_points' },
    { label: 'Resgates', key: 'total_claims' }, { label: 'Validados', key: 'total_validations' },
    { label: 'Pendentes', key: 'pending_claims' },
    { label: 'Categorias', get: r => (r.categories_accessed || []).join(', ') },
    { label: 'Cadastro', key: 'created_at' }
  ]);

  const exportMerchants = () => exportCSV(merchantAnalytics, 'lojistas', [
    { label: 'Empresa', key: 'company_name' }, { label: 'CNPJ', key: 'cnpj' },
    { label: 'Nome', key: 'name' }, { label: 'Contato', key: 'contact_name' },
    { label: 'Email', key: 'email' }, { label: 'WhatsApp', key: 'whatsapp' },
    { label: 'Cidade', key: 'city' }, { label: 'Bairro', key: 'neighborhood' },
    { label: 'Estado', key: 'state' }, { label: 'Endereco', key: 'commercial_address' },
    { label: 'Plano', key: 'plan' }, { label: 'Cupons', key: 'total_coupons' },
    { label: 'Ativos', key: 'active_coupons' }, { label: 'Resgates', key: 'total_claims' },
    { label: 'Validados', key: 'total_validations' },
    { label: 'Categorias', get: r => (r.categories || []).join(', ') },
    { label: 'Cadastro', key: 'created_at' }
  ]);

  const exportCoupons = () => exportCSV(couponAnalytics, 'cupons', [
    { label: 'Titulo', key: 'title' }, { label: 'Categoria', key: 'category' },
    { label: 'Cidade', key: 'city' }, { label: 'Estado', key: 'state' },
    { label: 'Desconto %', key: 'discount_percentage' }, { label: 'Lojista', key: 'merchant_name' },
    { label: 'Plano Lojista', key: 'merchant_plan' }, { label: 'Enviados', key: 'total_claims' },
    { label: 'Validados', key: 'total_validations' }, { label: 'Pendentes', key: 'pending_claims' },
    { label: 'Ativo', get: r => r.is_active && !r.is_deleted ? 'Sim' : 'Nao' },
    { label: 'Criado', key: 'created_at' }
  ]);

  const exportCategories = () => exportCSV(categoryAnalytics, 'categorias', [
    { label: 'Categoria', key: 'category' }, { label: 'Total Cupons', key: 'total_coupons' },
    { label: 'Ativos', key: 'active_coupons' }, { label: 'Resgates', key: 'total_claims' },
    { label: 'Validados', key: 'total_validations' }, { label: 'Pendentes', key: 'pending_claims' },
    { label: 'Conversao %', key: 'conversion_rate' }
  ]);

  const handleUploadBanner = async () => {
    if (!bannerForm.title || !bannerImage) { toast.error('Preencha titulo e selecione uma imagem'); return; }
    if (bannerForm.tipo !== 'global' && bannerForm.tipo !== 'padrao' && (!bannerForm.state || !bannerForm.city)) { toast.error('Estado e Cidade são obrigatórios para banners segmentados'); return; }
    if (bannerForm.tipo === 'cidade_categoria' && !bannerForm.category) { toast.error('Categoria obrigatoria para tipo Cidade+Categoria'); return; }
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      Object.entries(bannerForm).forEach(([k, v]) => fd.append(k, v));
      fd.append('image', bannerImage);
      await axios.post(`${BACKEND_URL}/api/admin/banners`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Banner criado com sucesso');
      setBannerForm({ title: '', url: '', description: '', tipo: 'global', ordem_exibicao: 1, state: '', city: '', category: '', merchant_name: '', contact_whatsapp: '', monthly_value: '', months_contracted: 1, renewal_alert_days: 7 });
      setBannerImage(null);
      setBannerCities([]);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar banner'); }
    finally { setUploadingBanner(false); }
  };

  const handleToggleBanner = async (b) => {
    try {
      const fd = new FormData();
      ['title', 'tipo', 'url', 'description', 'ordem_exibicao', 'state', 'city', 'category', 'merchant_name', 'contact_whatsapp', 'monthly_value', 'months_contracted', 'renewal_alert_days'].forEach(k => fd.append(k, b[k] ?? ''));
      fd.append('is_active', !b.is_active);
      await axios.put(`${BACKEND_URL}/api/admin/banners/${b.banner_id}`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(b.is_active ? 'Banner desativado' : 'Banner ativado');
      fetchData();
    } catch { toast.error('Erro ao atualizar banner'); }
  };

  const handleDeleteBanner = async (bannerId) => {
    try { await axios.delete(`${BACKEND_URL}/api/admin/banners/${bannerId}`, { headers }); toast.success('Banner excluido'); fetchData(); }
    catch { toast.error('Erro ao excluir'); }
  };

  const handleGeneratePayLink = async (bannerId) => {
    setGeneratingPayLink(bannerId);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/banners/${bannerId}/checkout`, { origin_url: window.location.origin }, { headers });
      const url = res.data.checkout_url;
      await navigator.clipboard.writeText(url);
      toast.success(`Link copiado! R$ ${res.data.amount.toFixed(2)} em ${res.data.months}x`);
      window.open(url, '_blank');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao gerar link'); }
    finally { setGeneratingPayLink(null); }
  };

  const handleManualActivate = async (bannerId) => {
    try { await axios.post(`${BACKEND_URL}/api/admin/banners/${bannerId}/activate`, {}, { headers }); toast.success('Banner ativado manualmente'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro ao ativar'); }
  };

  const handleToggleBannerVisibility = async (bannerId, currentHidden) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/banners/${bannerId}/toggle-visibility`, {}, { headers });
      toast.success(currentHidden ? 'Banner visível para consumidores' : 'Banner oculto para consumidores');
      fetchData();
    } catch { toast.error('Erro ao atualizar visibilidade'); }
  };

  const fetchBannerCities = async (uf) => {
    if (!uf) { setBannerCities([]); return; }
    try {
      const res = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`);
      const data = await res.json();
      setBannerCities(data.map(c => c.nome));
    } catch { setBannerCities([]); }
  };

  const RESTRICTED_TABS = ['keys', 'audiobooks', 'banners'];
  const isRestricted = adminUser?.admin_level === 'restricted';

  const tabs = [
    { key: 'overview', label: 'Visao Geral' },
    { key: 'financial', label: 'Financeiro' },
    { key: 'subscriptions', label: 'Assinaturas' },
    { key: 'users', label: 'Usuarios' },
    { key: 'merchants', label: 'Lojistas' },
    { key: 'influencers', label: 'Influenciadores' },
    { key: 'coupons', label: 'Cupons' },
    { key: 'categories', label: 'Categorias' },
    { key: 'keys', label: 'Chaves de Acesso' },
    { key: 'audiobooks', label: 'AudioBooks' },
    { key: 'banners', label: 'Patrocinadores' },
    { key: 'testimonials', label: 'Depoimentos' }
  ].filter(t => !isRestricted || !RESTRICTED_TABS.includes(t.key));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 shrink-0" />
            <h1 className="text-lg sm:text-2xl font-black text-slate-900 truncate" data-testid="admin-title">Painel Admin</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="admin-back-btn">
            <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Voltar</span>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 mb-6">
          <div className="flex gap-1.5 sm:gap-2 min-w-max sm:min-w-0 sm:flex-wrap pb-2 sm:pb-0">
            {tabs.map(tab => (
              <Button key={tab.key} variant={activeTab === tab.key ? 'default' : 'outline'} size="sm"
                onClick={() => { setActiveTab(tab.key); setSearchTerm(''); }}
                className={`text-xs sm:text-sm whitespace-nowrap ${activeTab === tab.key ? 'bg-slate-700 hover:bg-slate-800 border-slate-700' : 'border-slate-300'}`}
                data-testid={`tab-${tab.key}`}>
                {tab.label}
              </Button>
            ))}
          </div>
        </div>

        {activeTab === 'overview' && stats && (
          <AdminOverviewTab stats={stats} categoryAnalytics={categoryAnalytics} influencerAnalytics={influencerAnalytics}
            Icons={{ Users, UserCog, TrendingUp, Tag, ShoppingBag, CheckCircle }} />
        )}

        {activeTab === 'users' && (
          <AdminUsersTab userAnalytics={userAnalytics} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            sortField={sortField} sortDir={sortDir} toggleSort={toggleSort} sortData={sortData}
            expandedUser={expandedUser} setExpandedUser={setExpandedUser}
            setSelectedUser={setSelectedUser} setShowRoleDialog={setShowRoleDialog} setShowDeleteDialog={setShowDeleteDialog}
            exportUsers={exportUsers} />
        )}

        {activeTab === 'merchants' && (
          <AdminMerchantsTab merchantAnalytics={merchantAnalytics} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            expandedUser={expandedUser} setExpandedUser={setExpandedUser}
            setSelectedUser={setSelectedUser} setShowDeleteDialog={setShowDeleteDialog}
            exportMerchants={exportMerchants} />
        )}

        {activeTab === 'influencers' && (
          <AdminInfluencersTab influencerAnalytics={influencerAnalytics} expandedUser={expandedUser}
            setExpandedUser={setExpandedUser} handleDeleteUser={handleDeleteUser} exportCSV={exportCSV} />
        )}

        {activeTab === 'coupons' && (
          <AdminCouponsTab couponAnalytics={couponAnalytics} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            handleDeleteCoupon={handleDeleteCoupon} exportCoupons={exportCoupons} />
        )}

        {activeTab === 'categories' && (
          <AdminCategoriesTab categoryAnalytics={categoryAnalytics} exportCategories={exportCategories} />
        )}

        {activeTab === 'keys' && (
          <AdminKeysTab accessKeys={accessKeys} newKeyPlan={newKeyPlan} setNewKeyPlan={setNewKeyPlan}
            handleCreateKey={handleCreateKey} handleDeleteKey={handleDeleteKey}
            influencerInvites={influencerInvites} newInviteName={newInviteName} setNewInviteName={setNewInviteName}
            handleCreateInvite={handleCreateInvite} handleDeleteInvite={handleDeleteInvite}
            virtualKeyPlan={virtualKeyPlan} setVirtualKeyPlan={setVirtualKeyPlan}
            handleGenerateVirtualKey={handleGenerateVirtualKey} generatedVirtualKey={generatedVirtualKey} />
        )}

        {activeTab === 'subscriptions' && subHealth && (
          <AdminSubscriptionsTab subHealth={subHealth} />
        )}

        {activeTab === 'financial' && financialData && (
          <AdminFinancialTab financialData={financialData} />
        )}

        {activeTab === 'audiobooks' && (
          <AdminAudiobooksTab audiobooksMerchant={audiobooksMerchant} audiobooksInfluencer={audiobooksInfluencer}
            uploadingAB={uploadingAB} handleUploadAudiobook={handleUploadAudiobook} handleDeleteAudiobook={handleDeleteAudiobook}
            tutorialVideo={tutorialVideo} uploadingTutorial={uploadingTutorial}
            handleUploadTutorial={handleUploadTutorial} handleToggleTutorial={handleToggleTutorial} handleDeleteTutorial={handleDeleteTutorial} />
        )}

        {activeTab === 'banners' && (
          <AdminBannersTab banners={banners} bannerForm={bannerForm} setBannerForm={setBannerForm}
            bannerImage={bannerImage} setBannerImage={setBannerImage}
            uploadingBanner={uploadingBanner} handleUploadBanner={handleUploadBanner}
            handleToggleBanner={handleToggleBanner} handleDeleteBanner={handleDeleteBanner}
            handleGeneratePayLink={handleGeneratePayLink} handleManualActivate={handleManualActivate}
            generatingPayLink={generatingPayLink} bannerCities={bannerCities} fetchBannerCities={fetchBannerCities}
            handleToggleBannerVisibility={handleToggleBannerVisibility} />
        )}

        {activeTab === 'testimonials' && (
          <AdminTestimonialsTab testimonials={adminTestimonials} onRefresh={fetchData} />
        )}

        {/* Dialogs */}
        <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
          <DialogContent className="!bg-white max-w-sm">
            <DialogHeader><DialogTitle className="text-lg font-bold text-slate-900">Alterar Role</DialogTitle></DialogHeader>
            {selectedUser && (
              <div className="space-y-3 mt-2">
                <p className="text-sm text-slate-600">Usuario: <strong>{selectedUser.name}</strong></p>
                <p className="text-sm text-slate-600">Role atual: <strong>{selectedUser.role || 'user'}</strong></p>
                <div className="grid grid-cols-3 gap-2">
                  {['user', 'merchant', 'admin'].map(role => (
                    <Button key={role} size="sm" onClick={() => handleChangeRole(selectedUser.user_id, role)}
                      className={`capitalize ${role === (selectedUser.role || 'user') ? 'bg-slate-400' : 'bg-slate-600 hover:bg-slate-700'} text-white`}
                      disabled={role === (selectedUser.role || 'user')} data-testid={`set-role-${role}`}>
                      {role}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="!bg-white max-w-sm">
            <DialogHeader><DialogTitle className="text-lg font-bold text-red-600">Excluir Usuario</DialogTitle></DialogHeader>
            {selectedUser && (
              <div className="space-y-4 mt-2">
                <p className="text-sm text-slate-600">
                  Tem certeza que deseja excluir <strong>{selectedUser.name}</strong>?
                  Todos os dados serao removidos permanentemente.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowDeleteDialog(false)}
                    className="border-slate-300" data-testid="cancel-delete-btn">Cancelar</Button>
                  <Button size="sm" onClick={() => handleDeleteUser(selectedUser.user_id)}
                    className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
                    Excluir
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
